/**
 * GlobalState.messages 切片与 Worker 时间线接线（任务 9c）。
 *
 * 打开中会话只有一个（两端布局都如此：移动=栈顶 chat、桌面=右栏），切片只存
 * 它的时间线。数据源三条，全部落到「按 id 合并」一个口径（mergeTimelineRows）：
 * - 打开/切换会话（nav 的 activeChatKey 驱动，openChat/返回/深链/popstate 全覆盖）
 *   → call 'chat/timeline/open' 首屏切片（本地优先，场景 D/F 零 HTTP）；
 * - 推送 'chat/timeline'：append=WS 新消息批（同 id 行=43 隐蔽改单就地替换）；
 *   invalidate=顶部洞补完 → 以当前已加载行数重调 open 重读合并视图（零 HTTP）；
 * - loadOlder（UI 触顶）→ call 'chat/timeline/older' 向上翻页。
 *
 * 跳转定位（14.6c，引用/置顶/@ 共用）：目标不在已加载窗口时 ensureMessageLoaded
 * 以 loadOlder 同一条 older 通道**连续补拉**到目标进窗口（本地命中零 HTTP），
 * 封顶 JUMP_MAX_PAGES 页。私聊群聊同策，刻意不用后端 around——群聊 around 退化
 * 为只回 pin 快照（protocol §6），且 around 页会破坏本切片的连续窗口假设。
 *
 * 顺序正确性（contract.TimelinePush 约定）：响应与推送都产自 Worker 的 DB 写
 * 串行链，到达序 = 链序。rows 仍 null（首屏未回包）时丢弃 append——open 读在
 * 链上排在该批之后，回包必含它。epoch 只在 key 变化时自增（登出/切会话使在途
 * 响应过期）；同 key 的 reload 与 loadOlder 并发无需互斥——按 id 合并可交换。
 *
 * 已读（10c）：主线程只报「看到了 id」——打开中会话可见且 rows 里出现更大的
 * 对方消息 id 时调 'chat/read'（防抖/水位/WS 帧都在 Worker，见 session.noteRead）。
 * 触发点：open 回包、append 推送、页面回到可见、以及对账快照让打开中会话未读
 * 「复活」（read 帧曾在断线窗口丢失，force 重报由 Worker 水位去重）。
 * document 隐藏时不报（挂后台不算看到，回前台经 visibilitychange 补报）。
 *
 * 发送（10b）：outbox 切片 = 打开中会话的 pending/failed 本地消息（Worker
 * outbox.ts 状态机的镜像）。合并口径见 contract.OutboxPush：upsert 按 clientId
 * 替换/追加；sent **不立即删行**（先于 append 到达，立删闪一帧空档）——pending
 * 行保留到 rows 出现同 uuid（=clientId）的服务端行（reconcileOutbox，append/
 * open 回包两处都过）；open 回包的 outbox 快照整体替换切片（快照晚于此前推送，
 * 不回退；Worker 崩溃重建后快照为空，正确清掉已丢失的 pending 态）。
 */

import { activeChatKey } from '~/nav/projection';
import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type {
  ComposerRestriction,
  ConversationKey,
  ForwardResult,
  GroupMember,
  OutboxEntry,
  OutboxPush,
  PinnedMessage,
  SendInput,
  StoredMessage,
  TimelinePush,
} from '~/worker/contract';

export interface TimelineState {
  readonly key: ConversationKey;
  /** null = 首屏切片未回包（UI 渲染加载态不闪空）；升序连续区间（最新端对齐） */
  readonly rows: readonly StoredMessage[] | null;
  /** 已确认到历史起点，UI 停止触顶加载。只增不回退（reload 返回最新页不重置） */
  readonly reachedStart: boolean;
  /** 向上翻页在途（UI 防重复触发；同 key 同时只有一个 older 请求） */
  readonly loadingOlder: boolean;
  /** 本会话的乐观发送条目（pending/failed，按创建序；文件头合并口径） */
  readonly outbox: readonly OutboxEntry[];
  /**
   * 编辑异步失败计数（14.7；'chat/editFailed' 推送每次 +1）。行本身已被 append
   * 推送回滚还原，UI 只需对 seq 变化 toast「编辑失败」（14.7b）。
   */
  readonly editFailedSeq: number;
  /** 最近一次编辑失败的服务端原文（21f）；空串=无文案，UI 回落「编辑失败」 */
  readonly editFailedText: string;
  /**
   * 置顶列表（14.10；msgId 升序快照）。null=未加载/加载失败（UI 不渲染横幅）；
   * loadPins 回包写入，后续变更走 'chat/pins' 推送整体替换。
   */
  readonly pins: readonly PinnedMessage[] | null;
  /** 置顶操作异步失败计数（'chat/pinFailed' 推送每次 +1；行已被 append 推送回滚） */
  readonly pinFailedSeq: number;
  /**
   * 端到端加密态（14.14a；私聊且本机存有密钥）。open 回包写入；变更只会由本端
   * setEncryptionKey 发起（密钥纯本地无推送通道），set 回包当场更新。
   */
  readonly encryptionOn: boolean;
  /**
   * 群输入栏限制态（14.15a；groupRestrictionModel 口径）。null=未加载/加载失败
   * →UI 按不限制渲染（fail-open，服务端兜底拒发）；loadComposer 回包写入，
   * 禁言信令/群资料变更走 'chat/composer' 推送替换。私聊恒 null。
   */
  readonly composer: ComposerRestriction | null;
}

export interface MessagesSlice {
  /** null = 没有打开中的会话 */
  readonly timeline: TimelineState | null;
}

export const MESSAGES_INITIAL: MessagesSlice = { timeline: null };

/** 首屏/翻页每页行数（9e UI 若需调整在此单点改） */
export const TIMELINE_PAGE_SIZE = 30;

/**
 * 跳转补拉封顶页数（14.6c；预算=budgets.jumpToMessage）。超出即当「原消息不可见」
 * 降级 toast——10 页 × 30 行 = 300 行深度，引用/置顶目标通常都在近端；群聊长期
 * 本地存储命中率高，多数页零 HTTP。
 */
export const JUMP_MAX_PAGES = 10;

/**
 * 两段升序时间线按 id 合并，`fresh` 同 id 覆盖 `base`（后写的是刚落库/刚读库
 * 的更新值）。两输入都升序（库序/帧批序保证），线性归并。
 */
export function mergeTimelineRows(
  base: readonly StoredMessage[],
  fresh: readonly StoredMessage[],
): StoredMessage[] {
  if (base.length === 0) return [...fresh];
  if (fresh.length === 0) return [...base];
  const out: StoredMessage[] = [];
  let i = 0;
  let j = 0;
  while (i < base.length || j < fresh.length) {
    const a = base[i];
    const b = fresh[j];
    if (b === undefined) {
      out.push(a!);
      i += 1;
    } else if (a === undefined || a.id > b.id) {
      out.push(b);
      j += 1;
    } else if (a.id < b.id) {
      out.push(a);
      i += 1;
    } else {
      out.push(b); // 同 id：fresh 覆盖（隐蔽改单就地替换）
      i += 1;
      j += 1;
    }
  }
  return out;
}

/**
 * rows 里已出现同 uuid 服务端行的 outbox 条目移除（回显已落库可见，本地行退场）。
 * 从尾部扫 rows——回显必在最新端；outbox 非空是短暂态，平时零开销。
 */
export function reconcileOutbox(
  rows: readonly StoredMessage[],
  outbox: readonly OutboxEntry[],
): readonly OutboxEntry[] {
  if (outbox.length === 0) return outbox;
  const kept = outbox.filter((e) => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i]!.uuid === e.clientId) return false;
    }
    return true;
  });
  return kept.length === outbox.length ? outbox : kept;
}

/** append 推送应用纯函数。key 不符/未打开/首屏未回包返回原引用（契约边界） */
export function applyTimelineAppend(
  timeline: TimelineState | null,
  push: Extract<TimelinePush, { kind: 'append' }>,
): TimelineState | null {
  if (!timeline || timeline.key !== push.key || timeline.rows === null) return timeline;
  const rows = mergeTimelineRows(timeline.rows, push.rows);
  return { ...timeline, rows, outbox: reconcileOutbox(rows, timeline.outbox) };
}

/** remove 推送应用纯函数（服务端删除，行退场）。key 不符/未打开/首屏未回包返回原引用 */
export function applyTimelineRemove(
  timeline: TimelineState | null,
  push: Extract<TimelinePush, { kind: 'remove' }>,
): TimelineState | null {
  if (!timeline || timeline.key !== push.key || timeline.rows === null) return timeline;
  const gone = new Set(push.ids);
  const rows = timeline.rows.filter((r) => !gone.has(r.id));
  return rows.length === timeline.rows.length ? timeline : { ...timeline, rows };
}

/** outbox 推送应用纯函数（合并口径见文件头）。key 不符/首屏未回包返回原引用 */
export function applyOutboxPush(
  timeline: TimelineState | null,
  push: OutboxPush,
): TimelineState | null {
  if (!timeline || timeline.rows === null) return timeline;
  const key = push.type === 'upsert' ? push.entry.conversationKey : push.conversationKey;
  if (key !== timeline.key) return timeline;
  if (push.type === 'upsert') {
    const idx = timeline.outbox.findIndex((e) => e.clientId === push.entry.clientId);
    const outbox =
      idx >= 0
        ? timeline.outbox.map((e, i) => (i === idx ? push.entry : e))
        : [...timeline.outbox, push.entry];
    return { ...timeline, outbox };
  }
  if (push.type === 'removed') {
    return { ...timeline, outbox: timeline.outbox.filter((e) => e.clientId !== push.clientId) };
  }
  // sent：常规路径等 append 里的同 uuid 行到达再退场（立删闪空档）；迟到回显
  // （行早已在 rows）当场退场
  return { ...timeline, outbox: reconcileOutbox(timeline.rows, timeline.outbox) };
}

const KEY_RE = /^(dm|group):\d+$/;

/** nav 的 conversationKey 是宽 string（路由层不认识业务形状），这里收窄 */
function asConversationKey(raw: string | undefined): ConversationKey | null {
  return raw !== undefined && KEY_RE.test(raw) ? (raw as ConversationKey) : null;
}

/** 发送草稿（会话 key 取打开中会话，调用方不传）。content 构造归 Composer（10d） */
export interface SendDraft {
  readonly msgType: number;
  readonly content: string;
  readonly ext?: string;
}

/** 图片发送结果（UI 提示文案映射见 composerModel.imageSendErrorText） */
export type ImageSendOutcome = 'ok' | 'empty' | 'too-large' | 'no-chat' | 'error';

export interface MessagesConnection {
  /** 向上翻历史（UI 触顶调用）。未打开/在途/已到头/首屏未回包时 no-op */
  loadOlder(): void;
  /**
   * 把时间线窗口向更早方向连续补拉到包含 msgId（14.6c 跳转定位，文件头口径）。
   * 'ok'=目标行已在 rows（调用方再 scrollToKey）；'missing'=到头/封顶/已删/
   * 中途切会话。补拉期间 loadingOlder=true（UI 加载条复用、触顶门控静默）。
   */
  ensureMessageLoaded(msgId: number): Promise<'ok' | 'missing'>;
  /**
   * 乐观发送到打开中会话（无打开中会话 resolve false）。状态变化走 chat/outbox
   * 推送。resolve true=帧已写出（entry pending，不等回显——14.16b 贴纸发后记
   * 最近发送按此口径）；false=未发出（WS 断=failed 条目/无会话/RPC 异常）。
   */
  send(draft: SendDraft): Promise<boolean>;
  /**
   * 向**任意**会话发消息（14.21b 分享名片首用；chat/send 契约本就带
   * conversationKey，零 Worker 改动）。目标会话未打开时乐观条目不渲染
   * （outbox 推送按打开中会话过滤），回显经 WS 回流 bump 会话行；恰是
   * 打开中会话时与 send 同口径本地上屏。
   */
  sendTo(key: ConversationKey, draft: SendDraft): Promise<boolean>;
  /**
   * 图片发送到打开中会话（12b）：受理后 uploading 乐观气泡立即出现（blob URL
   * 预览），上传/发帧进展走 chat/outbox 推送。返回受理结果供 UI 提示（too-large
   * 等拒绝不入列）。ext（14.1b 相册字段）原样透传（''/缺省=单图）。
   */
  sendImage(file: File, ext?: string): Promise<ImageSendOutcome>;
  /**
   * 发图到**任意**会话（14.32 二维码分享首用；chat/sendImage 契约本就带 key，
   * 零 Worker 改动——sendTo 同理由）。目标非打开中会话时乐观条目不渲染
   * （outbox 推送按打开中会话过滤），恰是打开中会话时与 sendImage 同口径上屏。
   */
  sendImageTo(key: ConversationKey, file: File): Promise<ImageSendOutcome>;
  /**
   * 语音发送到打开中会话（14.2b）：录制完成的音频 Blob 原件直传，受理即出
   * uploading 乐观气泡（本地 blob URL 可当场试听）。结果口径同 sendImage。
   */
  sendVoice(blob: Blob, durationMs: number): Promise<ImageSendOutcome>;
  /**
   * 文件/视频发送到打开中会话（14.3b）：原件直传，受理即出 uploading 乐观卡片
   * （本地 blob URL 可当场另存/播放）。fileKind 由调用方分流（fileSend.ts）。
   * 结果口径同 sendImage。
   */
  sendFile(file: File, fileKind: 'file' | 'video'): Promise<ImageSendOutcome>;
  /** 重试 failed 条目（复用 clientId，气泡不重挂） */
  retrySend(clientId: string): void;
  /** 删除 failed 条目 */
  discardSend(clientId: string): void;
  /**
   * 服务端删除打开中会话的消息（11c）。成功后行退场走 remove 推送，resolve true；
   * HTTP 失败 reject（调用方提示）。无打开中会话 resolve false。
   */
  deleteMessages(msgIds: readonly number[]): Promise<boolean>;
  /**
   * 删除打开中群会话里某成员的全部消息（21j-1；群主/管理员）。
   * kickAndBlack=true 同时踢出 + 进群黑名单。返回删掉的条数；
   * null = 失败/非群会话（调用方当场提示）。
   */
  deleteUserMessages(userId: number, kickAndBlack: boolean): Promise<number | null>;
  /**
   * 编辑打开中会话的消息（14.7；仅自己的文本，menuModel 已挡、Worker 兜底）。
   * resolve false=未受理（WS 断/校验不过/无打开中会话），调用方当场提示；
   * 受理后乐观替换走 append 推送，异步失败走 editFailedSeq。
   */
  editMessage(msgId: number, content: string): Promise<boolean>;
  /**
   * 转发打开中会话的消息到若干目标会话（14.8；200ms 串行重发帧，整批发完才
   * resolve）。null=无打开中会话/RPC 异常；failed>0=有帧未发出（UI 提示失败）。
   */
  forwardMessages(
    msgIds: readonly number[],
    targets: readonly ConversationKey[],
  ): Promise<ForwardResult | null>;
  /**
   * 拉打开中会话的置顶列表（14.10；Worker 会话期缓存命中零 HTTP）。回包写进
   * timeline.pins（横幅渲染源）；失败/无打开中会话 resolve false（pins 保持
   * null，UI 不渲染横幅，重开重试）。14.10b 由横幅挂载时调用。
   */
  loadPins(): Promise<boolean>;
  /**
   * 拉打开中群会话的输入栏限制态（14.15a；Worker 会话期缓存命中零 HTTP）。回包
   * 写进 timeline.composer（禁言栏/媒体拦截渲染源）；失败/非群会话 resolve false
   * （composer 保持 null，UI 按不限制渲染 fail-open，服务端兜底；重开重试）。
   * 14.15b 由 ChatPage/Composer 挂载时调用。
   */
  loadComposer(): Promise<boolean>;
  /**
   * 拉打开中群会话的全量成员（14.11；@面板来源，Worker TTL 60s 缓存命中零
   * HTTP）。瞬态数据不进 GlobalState——面板打开现读、关闭即弃，调用方自持
   * 组件态。null=失败/非群会话/无打开中会话（面板提示重试）。
   */
  loadGroupMembers(): Promise<readonly GroupMember[] | null>;
  /**
   * 置顶/取消置顶打开中会话的消息（14.10；乐观，session.pinMessage 口径）。
   * false=未发出（WS 断/行不存在/已一致/在途），调用方当场提示；受理后行
   * pin 标记走 append 推送、置顶列表走 chat/pins 推送，异步失败走 pinFailedSeq。
   */
  pinMessage(msgId: number, pin: boolean): Promise<boolean>;
  /**
   * 设置打开中私聊会话的阅后即焚秒数（14.13a；second=0 关闭）。true=服务端已改
   * （会话 afterViewTime 走 conversations upsert 推送刷新）；false=失败/非私聊，
   * 本地不动。
   */
  setAfterView(second: number): Promise<boolean>;
  /**
   * 设置/清除打开中私聊会话的端到端加密密钥（14.14a；input=用户明文输入、
   * null/空串=关闭删除，派生与存储全在 Worker，零 HTTP）。回包更新
   * encryptionOn；已打开会话的行由 Worker 推 invalidate 按新密钥重读。
   * false=非私聊/无打开中会话/Worker 不可用。
   */
  setEncryptionKey(input: string | null): Promise<boolean>;
  /** Worker 崩溃重建后重开当前会话（重声明打开中 + 以已加载行数重读找平） */
  resync(): void;
  dispose(): void;
}

export function connectMessages(store: GlobalStore, host: AppWorkerHost): MessagesConnection {
  /** 只在 key 变化时自增（文件头口径）；在途响应以捕获时的值比对 */
  let epoch = 0;
  let lastKey: ConversationKey | null = null;
  /** 本会话已通知 Worker 的最大对方消息 id（切会话清零；权威水位在 Worker） */
  let lastNotedId = 0;

  function setTimeline(update: (t: TimelineState | null) => TimelineState | null): void {
    store.set((prev) => {
      const t = update(prev.messages.timeline);
      return t === prev.messages.timeline ? prev : { ...prev, messages: { timeline: t } };
    });
  }

  /** send/sendTo 共用：回包条目与 upsert 推送幂等（按 clientId 合并）；推送先到、回包补兜底 */
  function sendToKey(key: ConversationKey, draft: SendDraft): Promise<boolean> {
    const input: SendInput =
      draft.ext !== undefined
        ? { conversationKey: key, msgType: draft.msgType, content: draft.content, ext: draft.ext }
        : { conversationKey: key, msgType: draft.msgType, content: draft.content };
    return host.call('chat/send', input).then(
      (entry) => {
        // 乐观条目只上恰为打开中会话的屏（applyOutboxPush 自带 key 比对兜底）
        if (entry) {
          setTimeline((cur) => (cur && cur.key === key ? applyOutboxPush(cur, { type: 'upsert', entry }) : cur));
        }
        // true=帧已写出（pending）；WS 未就绪的立即 failed 条目算未发出（10a）
        return entry !== null && entry.status === 'pending';
      },
      () => false,
    );
  }

  /**
   * 已读触发器（文件头「已读」口径）：报最后一条对方消息 id。force=未读复活
   * 场景跳过本地水位（Worker 侧水位兜重复，重连后已清才真正补发）。
   */
  function maybeReportRead(force = false): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const s = store.get();
    const t = s.messages.timeline;
    if (!t || t.rows === null || s.auth.status !== 'logged-in') return;
    const myUid = s.auth.userId;
    let maxIncoming = 0;
    // 升序行从尾扫：第一条对方消息即最大 id
    for (let i = t.rows.length - 1; i >= 0; i -= 1) {
      const row = t.rows[i]!;
      if (row.id > 0 && row.userId !== myUid) {
        maxIncoming = row.id;
        break;
      }
    }
    if (maxIncoming <= 0 || (!force && maxIncoming <= lastNotedId)) return;
    lastNotedId = Math.max(lastNotedId, maxIncoming);
    void host.call('chat/read', { key: t.key, msgId: maxIncoming }).catch(() => undefined);
  }

  /**
   * fresh=true：切会话首屏（rows 整体写入）。fresh=false：invalidate/resync
   * 重读（按 id 合并进现有行，reachedStart 只增）。失败留现值：首屏失败留加载态
   * （重试交互归 9e），reload 失败旧行照常显示，下次推送/导航兜底。
   * outbox 两种情况都整体替换为回包快照（契约：快照晚于此前推送，全量不回退）。
   */
  function open(key: ConversationKey, pageSize: number, fresh: boolean): void {
    const seq = epoch;
    void host.call('chat/timeline/open', { key, pageSize }).then(
      (slice) => {
        if (seq !== epoch) return;
        setTimeline((t) => {
          if (!t || t.key !== key) return t;
          if (fresh || t.rows === null) {
            return {
              ...t,
              rows: slice.rows,
              reachedStart: slice.reachedStart,
              outbox: reconcileOutbox(slice.rows, slice.outbox),
              encryptionOn: slice.encryptionOn,
            };
          }
          const rows = mergeTimelineRows(t.rows, slice.rows);
          return {
            ...t,
            rows,
            reachedStart: t.reachedStart || slice.reachedStart,
            outbox: reconcileOutbox(rows, slice.outbox),
            encryptionOn: slice.encryptionOn,
          };
        });
        maybeReportRead();
      },
      () => undefined,
    );
  }

  // nav + auth 共同推导打开中会话：登录态下栈里最近的 chat 条目。选择器返回
  // 标量（Object.is 判重），popstate/手势返回/深链/登出路由变化全走同一条路。
  // needsPassword（锁在设密码页）不打开——守卫马上 replace 走，抢先发 msg/list
  // 纯浪费（与 nav/guard.ts 口径对齐）。
  function keyOf(s: GlobalState): ConversationKey | null {
    if (s.auth.status !== 'logged-in' || s.auth.needsPassword) return null;
    return asConversationKey(activeChatKey(s.nav));
  }

  function onKey(key: ConversationKey | null): void {
    if (key === lastKey) return;
    const prev = lastKey;
    lastKey = key;
    epoch += 1;
    lastNotedId = 0;
    if (key === null) {
      setTimeline(() => null);
      // 启动即 null（prev 也 null）不发多余 RPC；登出时 Worker session 已停，close 幂等
      if (prev !== null) void host.call('chat/timeline/close', null).catch(() => undefined);
      return;
    }
    setTimeline(() => ({
      key,
      rows: null,
      reachedStart: false,
      loadingOlder: false,
      outbox: [],
      editFailedSeq: 0,
      editFailedText: '',
      pins: null,
      pinFailedSeq: 0,
      encryptionOn: false,
      composer: null,
    }));
    open(key, TIMELINE_PAGE_SIZE, true);
  }

  const offPush = host.onPush('chat/timeline', (push) => {
    if (push.kind === 'append') {
      setTimeline((t) => applyTimelineAppend(t, push));
      maybeReportRead();
      return;
    }
    if (push.kind === 'remove') {
      setTimeline((t) => applyTimelineRemove(t, push));
      return;
    }
    const t = store.get().messages.timeline;
    if (!t || t.key !== push.key) return;
    if (push.kind === 'reset') {
      // 本地行被整体清空（清聊天记录/删好友/拉黑/35 信令）：**当场清屏，不重读**。
      // 两处都踩过坑（21ad 两轮人工实测）：
      // ① 推 invalidate 不行——它的重读按 id 并集合并，「行没了」表达不出来；
      // ② 改推 reset 后去 fresh 重读也不行——本地已空，Worker 的本地优先读会
      //    退化成 msg/list 回服务端拉一页（timeline.loadLatestSlice），把刚删的
      //    消息原样拉回屏上。本地清空就是清空，这里不需要任何读。
      // epoch 自增让在途 open/older 回包作废，旧切片不会盖回来。
      // reachedStart=true：空窗口不再触顶拉历史（下次进会话重开才按服务端真相读）。
      // outbox 刻意不动：pending/failed 是本地未发出的东西，不属于「聊天记录」，
      // 清掉会与 Worker 的 outbox 状态机脱钩。
      epoch += 1;
      setTimeline((cur) =>
        cur && cur.key === push.key
          ? { ...cur, rows: [], reachedStart: true, loadingOlder: false, pins: null }
          : cur,
      );
      return;
    }
    open(t.key, Math.max(t.rows?.length ?? 0, TIMELINE_PAGE_SIZE), false);
  });

  const offOutbox = host.onPush('chat/outbox', (push) => {
    setTimeline((t) => applyOutboxPush(t, push));
  });

  // 编辑异步失败（14.7）：行已被先行的 append 推送回滚还原，这里只递增 toast 信号
  const offEditFailed = host.onPush('chat/editFailed', (push) => {
    setTimeline((t) =>
      t && t.key === push.key
        ? { ...t, editFailedSeq: t.editFailedSeq + 1, editFailedText: push.message }
        : t,
    );
  });

  // 置顶列表整体替换（14.10）：信令/乐观操作/回滚驱动；首次读走 loadPins 回包
  const offPins = host.onPush('chat/pins', (push) => {
    setTimeline((t) => (t && t.key === push.key ? { ...t, pins: push.pins } : t));
  });

  const offPinFailed = host.onPush('chat/pinFailed', (push) => {
    setTimeline((t) =>
      t && t.key === push.key ? { ...t, pinFailedSeq: t.pinFailedSeq + 1 } : t,
    );
  });

  // 输入栏限制态变更（14.15a）：禁言信令/群资料变更驱动；首次读走 loadComposer 回包
  const offComposer = host.onPush('chat/composer', (push) => {
    setTimeline((t) => (t && t.key === push.key ? { ...t, composer: push.restriction } : t));
  });

  // 对账快照可能让打开中会话未读「复活」（read 帧曾在断线窗口丢失）：force 重报
  const offUnread = store.subscribe(
    (s) => s.conversations.rows,
    (rows) => {
      const t = store.get().messages.timeline;
      if (!t || rows === null) return;
      const row = rows.find((r) => r.key === t.key);
      if (row && (row.remote.unread > 0 || row.remote.isAt === 1)) maybeReportRead(true);
    },
  );

  const onVisibility = (): void => maybeReportRead();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  const offKey = store.subscribe(keyOf, onKey);
  onKey(keyOf(store.get()));

  /**
   * older 一页拉取并合并进当前窗口（loadOlder / ensureMessageLoaded 共用）。
   * true=已合并（空页也算成功）；false=HTTP 失败或 epoch 过期。不碰 loadingOlder，
   * 由调用方管生命周期。
   */
  function fetchOlderPage(key: ConversationKey, beforeId: number, seq: number): Promise<boolean> {
    return host.call('chat/timeline/older', { key, beforeId, pageSize: TIMELINE_PAGE_SIZE }).then(
      (slice) => {
        if (seq !== epoch) return false;
        setTimeline((cur) => {
          if (!cur || cur.key !== key || cur.rows === null) return cur;
          return {
            ...cur,
            rows: mergeTimelineRows(cur.rows, slice.rows),
            reachedStart: cur.reachedStart || slice.reachedStart,
          };
        });
        return true;
      },
      () => false,
    );
  }

  function setLoadingOlder(key: ConversationKey, value: boolean): void {
    setTimeline((cur) => (cur && cur.key === key ? { ...cur, loadingOlder: value } : cur));
  }

  return {
    loadOlder() {
      const t = store.get().messages.timeline;
      if (!t || t.rows === null || t.rows.length === 0 || t.loadingOlder || t.reachedStart) return;
      const { key } = t;
      const beforeId = t.rows[0]!.id;
      const seq = epoch;
      setLoadingOlder(key, true);
      void fetchOlderPage(key, beforeId, seq).then(() => {
        if (seq === epoch) setLoadingOlder(key, false);
      });
    },

    async ensureMessageLoaded(msgId) {
      // 终止判定：null=还要继续向上拉。窗口已覆盖 msgId（最旧行 id ≤ 目标）时
      // 直接按行存在与否定论——连续窗口假设下覆盖即应含，缺=已删。
      const seq = epoch;
      const verdictFor = (key: ConversationKey): 'ok' | 'missing' | null => {
        const t = store.get().messages.timeline;
        if (seq !== epoch || !t || t.key !== key || t.rows === null || t.rows.length === 0) {
          return 'missing';
        }
        if (t.rows[0]!.id <= msgId) {
          return t.rows.some((r) => r.id === msgId) ? 'ok' : 'missing';
        }
        return t.reachedStart ? 'missing' : null;
      };
      const t0 = store.get().messages.timeline;
      if (msgId <= 0 || !t0 || t0.rows === null || t0.rows.length === 0) return 'missing';
      const { key } = t0;
      const first = verdictFor(key);
      if (first !== null) return first;
      // 与在途 loadOlder 重叠最多重复一页（dedupe 兜底），合并可交换不乱序
      setLoadingOlder(key, true);
      try {
        for (let page = 0; page < JUMP_MAX_PAGES; page += 1) {
          const verdict = verdictFor(key);
          if (verdict !== null) return verdict;
          const beforeId = store.get().messages.timeline!.rows![0]!.id;
          if (!(await fetchOlderPage(key, beforeId, seq))) return 'missing';
        }
        return verdictFor(key) ?? 'missing';
      } finally {
        if (seq === epoch) setLoadingOlder(key, false);
      }
    },

    send(draft) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(false);
      return sendToKey(t.key, draft);
    },

    sendTo: (key, draft) => sendToKey(key, draft),

    sendImage(file, ext) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve('no-chat');
      const { key } = t;
      return host
        .call('chat/sendImage', {
          key,
          blob: file,
          filename: file.name || 'image',
          ...(ext !== undefined && ext !== '' ? { ext } : {}),
        })
        .then(
        (res) => {
          if (!res.accepted) return res.reason === 'no-session' ? 'no-chat' : res.reason;
          // 回包条目与 upsert 推送幂等（与 send 同口径：推送先到、回包兜底）
          setTimeline((cur) => applyOutboxPush(cur, { type: 'upsert', entry: res.entry }));
          return 'ok';
        },
        () => 'error',
      );
    },

    sendImageTo(key, file) {
      return host
        .call('chat/sendImage', { key, blob: file, filename: file.name || 'image' })
        .then(
          (res) => {
            if (!res.accepted) return res.reason === 'no-session' ? 'no-chat' : res.reason;
            // 目标恰是打开中会话才本地上屏（sendImage 同口径），否则等 WS 回流 bump 行
            const t = store.get().messages.timeline;
            if (t !== null && t.key === key) {
              setTimeline((cur) => applyOutboxPush(cur, { type: 'upsert', entry: res.entry }));
            }
            return 'ok';
          },
          () => 'error',
        );
    },

    sendVoice(blob, durationMs) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve('no-chat');
      return host.call('chat/sendVoice', { key: t.key, blob, durationMs }).then(
        (res) => {
          if (!res.accepted) return res.reason === 'no-session' ? 'no-chat' : res.reason;
          setTimeline((cur) => applyOutboxPush(cur, { type: 'upsert', entry: res.entry }));
          return 'ok';
        },
        () => 'error',
      );
    },

    sendFile(file, fileKind) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve('no-chat');
      return host
        .call('chat/sendFile', { key: t.key, blob: file, filename: file.name || 'file', fileKind })
        .then(
          (res) => {
            if (!res.accepted) return res.reason === 'no-session' ? 'no-chat' : res.reason;
            setTimeline((cur) => applyOutboxPush(cur, { type: 'upsert', entry: res.entry }));
            return 'ok';
          },
          () => 'error',
        );
    },

    retrySend(clientId) {
      void host.call('chat/send/retry', { clientId }).catch(() => undefined);
    },

    discardSend(clientId) {
      void host.call('chat/send/discard', { clientId }).catch(() => undefined);
    },

    deleteMessages(msgIds) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(false);
      return host.call('chat/delete', { key: t.key, msgIds });
    },

    deleteUserMessages(userId, kickAndBlack) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(null);
      // RPC/HTTP 异常折成 null（调用方提示重试），不向 UI 抛
      return host
        .call('chat/deleteUserMsgs', { key: t.key, userId, kickAndBlack })
        .catch(() => null);
    },

    editMessage(msgId, content) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(false);
      // RPC/Worker 异常折成未受理（调用方提示重试），不向 UI 抛
      return host.call('chat/edit', { key: t.key, msgId, content }).catch(() => false);
    },

    forwardMessages(msgIds, targets) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(null);
      return host
        .call('chat/forward', { sourceKey: t.key, msgIds, targets })
        .catch(() => null);
    },

    loadPins() {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(false);
      const { key } = t;
      const seq = epoch;
      return host.call('chat/pins/load', { key }).then(
        (pins) => {
          if (pins === null || seq !== epoch) return false;
          setTimeline((cur) => (cur && cur.key === key ? { ...cur, pins } : cur));
          return true;
        },
        () => false,
      );
    },

    pinMessage(msgId, pin) {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(false);
      return host.call('chat/pin', { key: t.key, msgId, pin }).catch(() => false);
    },

    setAfterView(second) {
      const t = store.get().messages.timeline;
      if (!t || !t.key.startsWith('dm:') || second < 0) return Promise.resolve(false);
      const friendId = Number(t.key.slice('dm:'.length));
      if (!Number.isFinite(friendId) || friendId <= 0) return Promise.resolve(false);
      // 会话 afterViewTime 直改由 Worker 走 'chat/conversations' upsert 推送，
      // 这里无本地切片要改；RPC/Worker 异常折 false（调用方提示重试）
      return host.call('chat/afterView/set', { friendId, second }).catch(() => false);
    },

    loadGroupMembers() {
      const t = store.get().messages.timeline;
      if (!t) return Promise.resolve(null);
      return host.call('chat/members/load', { key: t.key }).catch(() => null);
    },

    loadComposer() {
      const t = store.get().messages.timeline;
      // 私聊不发 RPC：恒不限制（composer 保持 null）
      if (!t || !t.key.startsWith('group:')) return Promise.resolve(false);
      const { key } = t;
      const seq = epoch;
      return host.call('chat/composer/load', { key }).then(
        (restriction) => {
          if (restriction === null || seq !== epoch) return false;
          setTimeline((cur) => (cur && cur.key === key ? { ...cur, composer: restriction } : cur));
          return true;
        },
        () => false,
      );
    },

    setEncryptionKey(input) {
      const t = store.get().messages.timeline;
      if (!t || !t.key.startsWith('dm:')) return Promise.resolve(false);
      const key = t.key;
      const friendId = Number(key.slice('dm:'.length));
      if (!Number.isFinite(friendId) || friendId <= 0) return Promise.resolve(false);
      // 行的重读由 Worker 推 invalidate（契约注释）；这里只找平开关态。
      // 失败（Worker 崩溃窗口）折 false 本地不动，调用方提示重试
      return host.call('chat/encryption/set', { friendId, key: input }).then(
        (on) => {
          setTimeline((cur) => (cur && cur.key === key ? { ...cur, encryptionOn: on } : cur));
          return true;
        },
        () => false,
      );
    },

    resync() {
      const t = store.get().messages.timeline;
      if (t) open(t.key, Math.max(t.rows?.length ?? 0, TIMELINE_PAGE_SIZE), t.rows === null);
    },

    dispose() {
      offPush();
      offOutbox();
      offEditFailed();
      offPins();
      offPinFailed();
      offComposer();
      offUnread();
      offKey();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
  };
}
